// src/pages/Lexique/LexiquePage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { getLexique } from "./../../services/lexiqueServices";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import { FaSearch, FaBookOpen } from "react-icons/fa";
import PageHeader from "../../components/Layout/PageHeader";

// 👉 adapte le chemin selon ton arborescence réelle
// ex : src/pages/Lexique/components/LexiqueRow.jsx
import LexiqueRow from "./LexiqueRow";

const strip = (s = "") =>
  s
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const LexiquePage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [lexique, setLexique] = useState([]);
  const [filteredLexique, setFilteredLexique] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedLetterIndex, setSelectedLetterIndex] = useState(0); // 0 = toutes lettres
  const [isLoading, setIsLoading] = useState(true);

  // Favoris en mémoire (simple, sans localStorage)
  const [favKeys, setFavKeys] = useState(() => new Set());

  const makeKey = (row) => `${row.japonais || ""}||${row.traduction || ""}`;

  const isFav = (row) => favKeys.has(makeKey(row));

  const toggleFavForRow = (row) => {
    setFavKeys((prev) => {
      const copy = new Set(prev);
      const key = makeKey(row);
      if (copy.has(key)) copy.delete(key);
      else copy.add(key);
      return copy;
    });
  };

  useEffect(() => {
    const fetchLexique = async () => {
      try {
        const data = await getLexique();
        const sorted = [...(data || [])].sort((a, b) =>
          (a.japonais || "").localeCompare(b.japonais || "", "fr", {
            sensitivity: "base",
          })
        );
        setLexique(sorted);
        setFilteredLexique(sorted);
      } catch (error) {
        console.error("Erreur lors de la récupération du lexique:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLexique();
  }, []);

  const getLetter = (index) => {
    if (index === 0) return "Toutes";
    return String.fromCharCode(64 + index); // 1 -> A, 2 -> B...
  };

  const filterLexique = (term, letterIndex) => {
    const normalizedTerm = strip(term);
    const letter =
      letterIndex === 0
        ? null
        : String.fromCharCode(64 + letterIndex).toLowerCase();

    const filtered = lexique.filter((entry) => {
      const jp = entry.japonais || "";
      const fr = entry.traduction || "";

      const matchesTerm =
        !normalizedTerm ||
        strip(jp).includes(normalizedTerm) ||
        strip(fr).includes(normalizedTerm);

      const matchesLetter = !letter || jp.toLowerCase().startsWith(letter);

      return matchesTerm && matchesLetter;
    });

    setFilteredLexique(filtered);
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    filterLexique(value, selectedLetterIndex);
  };

  const handleSliderChange = (event) => {
    const letterIndex = parseInt(event.target.value, 10);
    setSelectedLetterIndex(letterIndex);
    filterLexique(searchTerm, letterIndex);
  };

  const totalCount = useMemo(() => lexique.length, [lexique]);
  const filteredCount = useMemo(
    () => filteredLexique.length,
    [filteredLexique]
  );

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] dark:text-white">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Utilisateurs & Visiteurs"
        title="Lexique Judo Japonais / Français"
        subtitle="Découvrez les termes japonais utilisés en Judo avec leurs traductions françaises."
        align="center"
        size="lg"
        className="mb-6"
      />

      <InfoBubble title="Bienvenue sur la page Lexique">
        <p>
          Lexique des termes japonais utilisés en Judo, avec leur traduction
          française. Recherche par terme japonais ou français, et filtre
          alphabétique.
        </p>
        {/* <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
          Chaque ligne permet d&apos;écouter la prononciation en{" "}
          <span className="font-semibold">japonais</span> et en{" "}
          <span className="font-semibold">français</span>, de copier la paire
          et de l&apos;ajouter aux favoris (wanakana en import dynamique).
        </p> */}
      </InfoBubble>

      <div className="max-w-4xl mx-auto w-full mt-4">
        {/* Carte principale */}
        <div className="rounded-2xl bg-white/90 dark:bg-slate-900/90 border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden">
          {/* Header : recherche + filtre alphabet + stats */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-200 dark:border-slate-700 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {/* Recherche */}
            <div className="flex items-center w-full md:max-w-md bg-slate-100 dark:bg-slate-800 rounded-full px-3 py-2 gap-2">
              <FaSearch className="text-slate-500 dark:text-slate-300 text-sm" />
              <input
                type="text"
                placeholder="Rechercher (japonais ou français)..."
                value={searchTerm}
                onChange={handleSearch}
                className="bg-transparent border-none outline-none text-sm w-full placeholder:text-slate-400 dark:placeholder:text-slate-500"
              />
            </div>

            {/* Filtre alphabet + stats */}
            <div className="flex flex-col md:flex-row md:items-center md:gap-4 w-full md:w-auto">
              {/* Alpha slider */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <FaBookOpen className="text-primary" />
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-600 dark:text-slate-300">
                    Filtre alphabet (japonais)
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="range"
                    min={0}
                    max={26}
                    value={selectedLetterIndex}
                    onChange={handleSliderChange}
                    className="range range-xs range-warning w-40 md:w-48"
                  />
                  <span className="ml-1 text-sm font-semibold px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800">
                    {getLetter(selectedLetterIndex)}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="mt-2 md:mt-0 text-xs text-slate-600 dark:text-slate-300">
                <p>
                  <span className="font-semibold">{filteredCount}</span> terme
                  (s) affiché(s) sur{" "}
                  <span className="font-semibold">{totalCount}</span> au total.
                </p>
              </div>
            </div>
          </div>

          {/* Contenu liste */}
          <div className="max-h-[60vh] overflow-y-auto px-4 sm:px-6 py-3 sm:py-4">
            {isLoading ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-10 rounded-lg bg-slate-100 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : filteredLexique.length === 0 ? (
              <p className="text-sm text-slate-600 dark:text-slate-300 italic">
                Aucun terme trouvé pour ces critères.
              </p>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredLexique.map((row) => (
                  <LexiqueRow
                    key={makeKey(row)}
                    row={row}
                    q={searchTerm}
                    isFav={isFav(row)}
                    toggleFav={() => toggleFavForRow(row)}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LexiquePage;
